/*
 * ----------------------------------------------------------------------------------------------------------------
 * BONESABER GRID FILTER
 * ----------------------------------------------------------------------------------------------------------------
 *
 * Client-side certification/drop/length filter for sections/bonesaber-grid.liquid.
 *
 * WHY NOT REUSE 'usa-bats-grid-filter.js', WHICH ALREADY SERVES THREE PAGES: that engine is built around
 * exactly two facets — readActiveState() hardcodes { length, drop }, and cardMatchesState() branches on the
 * three USA card types. This page needs a third facet (certification), needs to hide a whole headed group when
 * its cards are all filtered out, and needs the drop row to appear only once a certification is chosen.
 * Bending the shared engine into that shape would mean rewriting its core in a file the live USA, BBCOR and
 * USSSA grids all depend on. A purpose-built file is the smaller risk, and it is the only one of the four with
 * a genuinely different filter model rather than the same model with different values.
 *
 * WHY THE DROP ROW IS PROGRESSIVE: drop is meaningless without certification on this page. A "-10" USA bat and
 * a "-10" USSSA bat are different products, which is why the tags read "-10 USA" and "-10 USSSA" rather than
 * "-10". Showing all eleven drops at once produces a wall of near-identical buttons where only a few are ever
 * valid together. So the row stays hidden until exactly one certification is selected, then shows only that
 * certification's drops, read from each button's own data-cert attribute.
 *
 * CERTIFICATION IS READ FROM THE GROUP, NOT THE CARD. Cards sit inside a [data-bonesaber-group] wrapper that
 * carries data-cert, so 'bat-line-card' and 'bat-plain-card' need no certification awareness at all and stay
 * shared with the USSSA grid unmodified.
 *
 * FACET DATA otherwise matches the existing engines — data-usa-bats-card on the card, data-usa-bats-facets
 * holding drop plus either a lengths array (plain) or a per-sibling map (line) — so the cards really are the
 * same cards, only the engine differs.
 */

(function () {
	'use strict';

	function init() {
		var root = document.querySelector('[data-bonesaber-root]');
		if (!root) return;

		var cards = buildCardModel(root);
		var filterInputs = Array.prototype.slice.call(root.querySelectorAll('[data-bonesaber-filter]'));
		var emptyState = root.querySelector('[data-bonesaber-empty-state]');
		var clearButton = root.querySelector('[data-bonesaber-clear]');

		filterInputs.forEach(function (input) {
			input.addEventListener('change', function () {
				apply(root, cards, filterInputs, emptyState);
			});
		});

		if (clearButton) {
			clearButton.addEventListener('click', function () {
				filterInputs.forEach(function (input) {
					input.checked = false;
				});
				apply(root, cards, filterInputs, emptyState);
			});
		}

		apply(root, cards, filterInputs, emptyState);
	}

	function normalize(value) {
		return (value || '').toString().trim().toLowerCase();
	}

	function buildCardModel(root) {
		var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-usa-bats-card]'));

		return cardEls.map(function (el) {
			var group = el.closest('[data-bonesaber-group]');
			var facetsScript = el.querySelector('[data-usa-bats-facets]');
			var facets = {};

			if (facetsScript) {
				try {
					facets = JSON.parse(facetsScript.textContent);
				} catch (e) {
					facets = {};
				}
			}

			var lengths = [];

			if (facets.siblings) {
				// Line card — union of every sibling's lengths, since any of them can be swatched to.
				Object.keys(facets.siblings).forEach(function (handle) {
					(facets.siblings[handle].lengths || []).forEach(function (l) {
						var n = normalize(l);
						if (lengths.indexOf(n) === -1) lengths.push(n);
					});
				});
			} else {
				lengths = (facets.lengths || []).map(normalize);
			}

			return {
				el: el,
				group: group,
				certification: normalize(group && group.getAttribute('data-cert')),
				drop: normalize(facets.drop || el.getAttribute('data-drop')),
				lengths: lengths
			};
		});
	}

	function readActiveState(filterInputs) {
		var state = { certification: new Set(), drop: new Set(), length: new Set() };

		filterInputs.forEach(function (input) {
			if (!input.checked) return;
			var facet = input.getAttribute('data-bonesaber-filter');
			if (state[facet]) state[facet].add(normalize(input.value));
		});

		return state;
	}

	function cardMatches(card, state) {
		if (state.certification.size > 0 && !state.certification.has(card.certification)) return false;
		if (state.drop.size > 0 && !state.drop.has(card.drop)) return false;

		if (state.length.size > 0) {
			var hit = card.lengths.some(function (l) {
				return state.length.has(l);
			});
			if (!hit) return false;
		}

		return true;
	}

	// Drop row: hidden entirely with no certification chosen or several chosen, since the buttons would be
	// ambiguous either way. With exactly one, show only that certification's own drops.
	function updateDropRow(root, state) {
		var group = root.querySelector('[data-bonesaber-drop-group]');
		if (!group) return;

		var single = state.certification.size === 1;
		var activeCert = single ? Array.from(state.certification)[0] : null;

		group.hidden = !single;

		Array.prototype.forEach.call(group.querySelectorAll('[data-cert]'), function (option) {
			var belongs = normalize(option.getAttribute('data-cert')) === activeCert;
			option.hidden = !belongs;

			// Uncheck a drop that no longer belongs, so a hidden button can't keep filtering invisibly.
			if (!belongs) {
				var input = option.querySelector('[data-bonesaber-filter="drop"]');
				if (input && input.checked) input.checked = false;
			}
		});
	}

	function updateGroupVisibility(root) {
		Array.prototype.forEach.call(root.querySelectorAll('[data-bonesaber-group]'), function (group) {
			var inGroup = Array.prototype.slice.call(group.querySelectorAll('[data-usa-bats-card]'));
			group.hidden = !inGroup.some(function (card) {
				return !card.hidden;
			});
		});
	}

	// Same dead-end prevention as the other engines: test each unchecked option against the other facets and
	// disable it if selecting it would return nothing. Its own facet is ignored, since same-facet checkboxes
	// are OR'd and can only add matches.
	function updateNarrowing(cards, filterInputs, live) {
		filterInputs.forEach(function (input) {
			var option = input.closest('.usa-bats-grid__filter-option');

			if (input.checked) {
				input.disabled = false;
				if (option) option.classList.remove('is-unavailable');
				return;
			}

			var facet = input.getAttribute('data-bonesaber-filter');
			var test = {
				certification: new Set(live.certification),
				drop: new Set(live.drop),
				length: new Set(live.length)
			};
			test[facet] = new Set([normalize(input.value)]);

			var hit = cards.some(function (card) {
				return cardMatches(card, test);
			});

			input.disabled = !hit;
			if (option) option.classList.toggle('is-unavailable', !hit);
		});
	}

	function apply(root, cards, filterInputs, emptyState) {
		var state = readActiveState(filterInputs);

		updateDropRow(root, state);

		// The drop row may have just unchecked a stale button, so re-read before filtering on it.
		state = readActiveState(filterInputs);

		var matches = 0;

		cards.forEach(function (card) {
			var ok = cardMatches(card, state);
			card.el.hidden = !ok;
			if (ok) matches++;
		});

		updateGroupVisibility(root);

		if (emptyState) emptyState.hidden = matches > 0;

		var clearButton = root.querySelector('[data-bonesaber-clear]');
		if (clearButton) {
			clearButton.hidden = state.certification.size + state.drop.size + state.length.size === 0;
		}

		updateNarrowing(cards, filterInputs, state);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();